import React, { useEffect, useState } from "react";
import { authHeader } from "../_helpers";
import axios from "axios";
import moment from "moment";
import Sidebar from "./component/Sidebar";
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Link } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export default function DepositAccounts() {
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFilings, setTotalFilings] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  // { open, target: filing|null, confirmText: string, busy: bool }
  const [del, setDel] = useState({ open: false, target: null, confirmText: "", busy: false });
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const openDelete = (filing) => setDel({ open: true, target: filing, confirmText: "", busy: false });
  const closeDelete = () => {
    if (del.busy) return;
    setDel({ open: false, target: null, confirmText: "", busy: false });
  };

  // Force-typed confirmation when status is anything other than submitted.
  const requiresTypedConfirm = del.target && del.target.status !== "submitted";
  const expectedConfirm = del.target?.siren ? String(del.target.siren) : "SUPPRIMER";
  const canConfirm = !requiresTypedConfirm || del.confirmText.trim() === expectedConfirm;

  const performAdminDelete = async () => {
    if (!del.target || !canConfirm) return;
    setDel((d) => ({ ...d, busy: true }));
    try {
      const resp = await axios.delete(
        `${process.env.REACT_APP_API_URL}api/admin/deposit-accounts/${del.target.id}`,
        { headers: authHeader() }
      );
      const inpiOk = resp.data?.inpi?.ok;
      setSnack({
        open: true,
        severity: "success",
        message: inpiOk
          ? "Dépôt supprimé (INPI inclus)"
          : "Dépôt supprimé (INPI non confirmé)",
      });
      setDel({ open: false, target: null, confirmText: "", busy: false });
      await fetchFilings();
    } catch (err) {
      setSnack({
        open: true,
        severity: "error",
        message: err.response?.data?.message || "Erreur lors de la suppression",
      });
      setDel((d) => ({ ...d, busy: false }));
    }
  };

  useEffect(() => {
    fetchFilings();
  }, [page, rowsPerPage, searchTerm]);

  const fetchFilings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/admin/deposit-accounts`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            search: searchTerm,
          },
          headers: authHeader(),
        }
      );

      if (response.data && response.data.success) {
        setFilings(response.data.data || []);
        setTotalFilings(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching deposit accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: "default",
      submitted: "primary",
      validated: "success",
      rejected: "error",
      pending: "warning",
    };
    return statusColors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: "Brouillon",
      signed: "Signé",
      submitted: "Soumis",
      validated: "Validé",
      rejected: "Rejeté",
      pending: "En attente",
    };
    return statusLabels[status] || status;
  };

  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      succeeded: "success",
      monthly: "info",
      pending: "warning",
      failed: "error",
    };
    return colors[paymentStatus] || "default";
  };

  const getPaymentStatusLabel = (paymentStatus) => {
    const labels = {
      succeeded: "Payé",
      monthly: "Forfait mensuel",
      pending: "En attente",
      failed: "Échoué",
    };
    return labels[paymentStatus] || paymentStatus || "En attente";
  };

  return (
    <div className="flex w-full h-screen overflow-hidden bg-gray-100">
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <Breadcrumbs aria-label="breadcrumb" className="mb-4">
              <Link to="/admin/home" className="text-blue-600 hover:underline">
                Tableau de bord
              </Link>
              <Typography color="text.primary">Dépôt des comptes</Typography>
            </Breadcrumbs>

            <div className="flex justify-between items-center">
              <Typography variant="h4" component="h1" className="font-bold">
                Dépôt des comptes annuels
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchFilings}
                disabled={loading}
                className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
              >
                Actualiser
              </Button>
            </div>
          </div>

          {/* Search */}
          <Box className="mb-4">
            <TextField
              fullWidth
              placeholder="Rechercher par SIREN, nom d'entreprise, ou utilisateur..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Entreprise</TableCell>
                    <TableCell>SIREN</TableCell>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Date de clôture</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Paiement</TableCell>
                    <TableCell>Montant</TableCell>
                    <TableCell>Date de soumission</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Aucun dépôt de comptes trouvé
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filings.map((filing) => (
                      <TableRow key={filing.id} hover>
                        <TableCell>{filing.id}</TableCell>
                        <TableCell>{filing.companyName || "N/A"}</TableCell>
                        <TableCell>{filing.siren || "N/A"}</TableCell>
                        <TableCell>
                          {filing.userFirstname} {filing.userLastname}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {filing.userEmail}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {filing.filingType}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {filing.liasseType}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {filing.closingDate
                            ? moment(filing.closingDate, "DD/MM/YYYY").format(
                                "DD/MM/YYYY"
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(filing.status)}
                            color={getStatusColor(filing.status)}
                            size="small"
                             sx={{
                            backgroundColor: "#1cbe93",   // change this
                            color: "white"
                          }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPaymentStatusLabel(filing.paymentStatus)}
                            color={getPaymentStatusColor(filing.paymentStatus)}
                            size="small"
                             sx={{
                            backgroundColor: "#1cbe93",   // change this
                            color: "white"
                          }}
                          />
                        </TableCell>
                        <TableCell>
                          {filing.amountPaid
                            ? `${parseFloat(filing.amountPaid).toFixed(2)} €`
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {filing.submittedAt
                            ? moment(filing.submittedAt).format(
                                "DD/MM/YYYY HH:mm"
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Link to={`/admin/deposit-accounts/${filing.id}`}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                              >
                                Voir
                              </Button>
                            </Link>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => openDelete(filing)}
                            >
                              Supprimer
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalFilings}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Lignes par page:"
            />
          </Paper>
        </div>
      </div>

      <Dialog
        open={del.open}
        onClose={closeDelete}
        disableEscapeKeyDown={del.busy}
        maxWidth="sm"
        fullWidth
        aria-labelledby="admin-delete-dialog-title"
      >
        <DialogTitle id="admin-delete-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="error" />
          Supprimer ce dépôt&nbsp;?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Box sx={{ mb: 2 }}>
              Vous êtes sur le point de supprimer définitivement le dépôt&nbsp;:
              <Box component="ul" sx={{ mt: 1, mb: 1 }}>
                <li>
                  <strong>{del.target?.companyName || "Entreprise"}</strong>
                  {del.target?.siren ? ` — SIREN ${del.target.siren}` : ""}
                </li>
                <li>
                  Statut actuel&nbsp;:{" "}
                  <Chip
                    label={getStatusLabel(del.target?.status)}
                    color={getStatusColor(del.target?.status)}
                    size="small"
                    sx={{ ml: 0.5 }}
                  />
                </li>
                {del.target?.userEmail && <li>Utilisateur&nbsp;: {del.target.userEmail}</li>}
              </Box>
            </Box>

            {requiresTypedConfirm ? (
              <MuiAlert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />} sx={{ mb: 2 }}>
                Ce dépôt n'est pas au statut « Soumis ». La suppression peut
                rendre les données comptables incohérentes (paiements, factures,
                pièces jointes). Tapez le SIREN <strong>{expectedConfirm}</strong>{" "}
                ci-dessous pour confirmer.
              </MuiAlert>
            ) : (
              <MuiAlert severity="info" sx={{ mb: 2 }}>
                Les pièces jointes seront également supprimées. Une tentative
                de suppression sera envoyée à l'INPI (résultat non garanti).
              </MuiAlert>
            )}

            {requiresTypedConfirm && (
              <TextField
                autoFocus
                fullWidth
                size="small"
                label={`Tapez ${expectedConfirm} pour confirmer`}
                value={del.confirmText}
                onChange={(e) => setDel((d) => ({ ...d, confirmText: e.target.value }))}
                disabled={del.busy}
              />
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={del.busy}>
            Annuler
          </Button>
          <Button
            onClick={performAdminDelete}
            color="error"
            variant="contained"
            disabled={del.busy || !canConfirm}
            startIcon={del.busy ? <CircularProgress size={14} sx={{ color: "white" }} /> : <DeleteIcon />}
          >
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
